/* Modenaire editorial layer (Oct 2026): what every md-* section shares, plus the new header and menu.
   Ported from the press page (assets/press-page.js) without its page-wide side effects: one passive scroll loop,
   no smooth-scroll on <html>, no inline overflow on <html>, and every listener is removed when a section is
   re-rendered in the theme editor. Loaded once, deferred, by sections/md-header.liquid; safe to run twice. */
(() => {
  if (window.MD) return;
  const MD = (window.MD = {});
  const reduce = (MD.reduce = matchMedia('(prefers-reduced-motion: reduce)'));
  MD.design = !!(window.Shopify && window.Shopify.designMode);
  if (MD.design) document.documentElement.classList.add('md-design');
  const clamp = (MD.clamp = (v, a, b) => Math.min(b, Math.max(a, v)));
  MD.mod = (a, n) => ((a % n) + n) % n;
  // slow connections and data saver: films stay as their cover photos
  MD.lite = () => { const c = navigator.connection; return !!(c && (c.saveData || /(^|-)(2g|3g)$/.test(c.effectiveType || ''))); };
  const T = () => window.theme || {};

  // ---------- one scroll loop for everything that moves with the page ----------
  const parts = new Set();
  let ticking = false;
  const run = () => {
    ticking = false;
    const vh = innerHeight;
    parts.forEach((p) => { try { p.frame(vh); } catch (e) { console.error(e); } });
  };
  MD.loop = {
    add(p) { parts.add(p); MD.loop.kick(); },
    remove(p) { parts.delete(p); },
    kick() { if (!ticking) { ticking = true; requestAnimationFrame(run); } },
    measure() { parts.forEach((p) => { if (p.measure) try { p.measure(); } catch (e) { console.error(e); } }); MD.loop.kick(); },
  };
  addEventListener('scroll', MD.loop.kick, { passive: true });
  let rz;
  addEventListener('resize', () => { clearTimeout(rz); rz = setTimeout(MD.loop.measure, 120); });
  addEventListener('load', MD.loop.measure);
  if (document.fonts) document.fonts.ready.then(MD.loop.measure);   // headings change height once the web fonts arrive

  // ---------- things rise into place as they arrive ----------
  let io = null, lio = null;
  MD.reveal = (root) => {
    const all = (sel) => (root.matches && root.matches(sel) ? [root] : []).concat([...root.querySelectorAll(sel)]);   // the section itself may be the one that moves
    const items = all('.md-rv, .md-swipe, .md-ribbon').filter((n) => !n.closest('.md-menu'));
    const lifts = all('.md-lift');
    if (reduce.matches || MD.design || !('IntersectionObserver' in window)) {
      items.forEach((n) => n.classList.add('in')); lifts.forEach((n) => n.classList.add('in')); return;
    }
    io = io || new IntersectionObserver((es) => es.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); } }), { rootMargin: '0px 0px -10% 0px' });
    lio = lio || new IntersectionObserver((es) => es.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('in'); lio.unobserve(e.target); } }), { rootMargin: '0px 0px -35% 0px' });
    items.forEach((n) => io.observe(n));
    lifts.forEach((n) => lio.observe(n));
  };

  // ---------- the ribbon: a strip of fabric drawn in fine lines, twisting as it crosses the page ----------
  MD.ribbon = ({ lines = 28, amp = 260, turns = 1.2, twist = 2.3, width = 170, y0 = 500, phase = 0 } = {}) => {
    const NS = 'http://www.w3.org/2000/svg', svg = document.createElementNS(NS, 'svg');
    svg.setAttribute('viewBox', '0 0 1600 1000'); svg.setAttribute('preserveAspectRatio', 'xMidYMid slice');
    svg.setAttribute('aria-hidden', 'true'); svg.setAttribute('focusable', 'false');
    for (let i = 0; i < lines; i++) {
      const k = i / (lines - 1) - 0.5;
      let d = '';
      for (let s = 0; s <= 96; s++) {
        const t = s / 96, x = -160 + t * 1920, a = t * Math.PI * turns + phase;
        const yc = y0 + amp * Math.sin(a), dy = ((amp * Math.PI * turns) / 1920) * Math.cos(a), len = Math.hypot(1, dy);
        const w = width * Math.cos(t * Math.PI * twist + phase * 0.7) * (0.55 + 0.45 * Math.sin(t * Math.PI));
        d += (s ? 'L' : 'M') + (x - (dy / len) * k * w).toFixed(1) + ' ' + (yc + (1 / len) * k * w).toFixed(1);
      }
      const path = document.createElementNS(NS, 'path');
      path.setAttribute('d', d); path.setAttribute('pathLength', '1'); path.style.setProperty('--d', (i * 0.035).toFixed(3) + 's');
      svg.append(path);
    }
    return svg;
  };
  const ribbons = new Set();
  MD.drawRibbons = (root) => {
    root.querySelectorAll('.md-ribbon[data-ribbon]').forEach((box) => {
      if (!box.querySelector('svg')) {
        let o = {};
        try { o = JSON.parse(box.dataset.ribbon || '{}'); } catch (e) { /* default ribbon */ }
        box.append(MD.ribbon(o));
      }
      if (!box.closest('.md-menu')) ribbons.add(box);
    });
  };
  MD.loop.add({
    frame(vh) {
      if (reduce.matches) return;
      ribbons.forEach((b) => {
        if (!b.isConnected) { ribbons.delete(b); return; }
        const r = b.getBoundingClientRect();
        if (r.bottom < 0 || r.top > vh) return;
        b.style.setProperty('--ry', (-(r.top / vh) * 30).toFixed(1) + 'px');
      });
    },
  });

  // ---------- small helpers ----------
  const scripts = {};
  MD.loadScript = (src) => scripts[src] || (scripts[src] = new Promise((res, rej) => {
    const s = document.createElement('script'); s.src = src; s.async = true; s.onload = res; s.onerror = rej; document.head.append(s);
  }));
  MD.lockScroll = (on) => { document.documentElement.classList.toggle('md-lock', !!on); };
  // a muted film plays only while it is on screen (and not after the shopper pressed pause)
  MD.playWhenVisible = (host, v, may) => host.io((es) => es.forEach((e) => {
    if (e.isIntersecting && !host.paused && document.visibilityState === 'visible' && (!may || may(v))) { const p = v.play(); if (p && p.catch) p.catch(() => {}); }
    else v.pause();
  }), { threshold: 0.05 }).observe(v);
  // the beige box behind a photo is only there while it loads (left in place it shows as a pale edge when zoomed)
  MD.settle = (root) => root.querySelectorAll('.md-frame img, .md-trio__fig img').forEach((img) => {
    const box = img.closest('.md-frame, .md-trio__fig'), done = () => box && box.classList.add('is-ld');
    if (img.complete && img.naturalWidth) done(); else img.addEventListener('load', done, { once: true });
  });

  // ---------- base for every md section: tidy setup and teardown, including theme editor reloads ----------
  class Section extends HTMLElement {
    connectedCallback() {
      if (this._live) return;
      this._live = true; this.ac = new AbortController(); this.obs = []; this.timers = [];
      this.classList.add('md-el');
      try { this.setup(); } catch (e) { console.error(e); }
    }
    disconnectedCallback() {
      this._live = false;
      if (this.ac) this.ac.abort();
      (this.obs || []).forEach((o) => o.disconnect());
      (this.timers || []).forEach((t) => { clearInterval(t); clearTimeout(t); });
      this.obs = []; this.timers = [];
      MD.loop.remove(this);
      if (this.teardown) try { this.teardown(); } catch (e) { console.error(e); }
    }
    on(target, type, fn, opts) { if (target) target.addEventListener(type, fn, Object.assign({ signal: this.ac.signal }, opts || {})); }
    io(cb, opts) { const o = new IntersectionObserver(cb, opts); this.obs.push(o); return o; }
    setup() {}
  }
  MD.Section = Section;
  MD.define = (name, cls) => { if (!customElements.get(name)) customElements.define(name, cls); };

  if (MD.design) {
    const elIn = (sec) => sec && sec.querySelector('.md-el');
    document.addEventListener('shopify:section:select', (e) => { const el = elIn(e.target); if (el && el.onSelect) el.onSelect(e); });
    document.addEventListener('shopify:section:deselect', (e) => { const el = elIn(e.target); if (el && el.onDeselect) el.onDeselect(e); });
    document.addEventListener('shopify:block:select', (e) => { const el = elIn(e.target.closest('.shopify-section')); if (el && el.onBlockSelect) el.onBlockSelect(e.target, e); });
    document.addEventListener('shopify:section:reorder', () => MD.loop.measure());
    document.addEventListener('shopify:section:load', () => setTimeout(MD.loop.measure, 50));
  }

  // ---------- cart: quick add from any md card, and the bag count in the header ----------
  const hashOf = async (cart) => {
    const str = cart.items.map((i) => `${i.key}|${i.quantity}`).join(',');
    if (!(window.crypto && crypto.subtle)) return '';
    const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(str));
    return [...new Uint8Array(buf)].map((x) => x.toString(16).padStart(2, '0')).join('');
  };
  MD.cart = {
    async refresh() {
      try {
        const res = await fetch(`${T().routes ? T().routes.cart : '/cart'}.js`, { headers: { Accept: 'application/json' } });
        const cart = await res.json();
        document.querySelectorAll('.md-header .cart-link').forEach((a) => {
          const n = a.querySelector('.cart-link__count');
          if (n) n.textContent = cart.item_count > 0 ? cart.item_count : '';
          if (a.dataset.label) a.setAttribute('aria-label', a.dataset.label.replace('[count]', cart.item_count));
        });
        const h = await hashOf(cart);
        if (h) document.querySelectorAll('.cart-link').forEach((a) => { a.dataset.hash = h; });   // main.js compares this on page show
      } catch (e) { /* the count catches up on the next page */ }
    },
    async add(id, opener, form) {
      const msg = form && form.querySelector('.md-add__msg');
      const say = (t, ok) => { if (msg) { msg.textContent = t; msg.classList.toggle('is-ok', !!ok); } };
      const drawer = document.querySelector('cart-drawer.js-cart-drawer');
      const body = { items: [{ id: Number(id), quantity: 1 }] };
      if (drawer && drawer.dataset.sectionId) { body.sections = drawer.dataset.sectionId; body.sections_url = location.pathname; }
      if (form) form.classList.add('is-busy');
      say('');
      try {
        const res = await fetch(T().routes ? T().routes.cartAdd : '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify(body),
        });
        const data = await res.json();
        if (!res.ok || data.status) throw new Error(data.description || data.message || 'Sorry, that size could not be added.');
        const html = drawer && data.sections && data.sections[drawer.dataset.sectionId];
        if (html && drawer.updateFromCartChange) drawer.updateFromCartChange(html);
        else document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
        (opener || document).dispatchEvent(new CustomEvent('on:cart:add', { bubbles: true, detail: { variantId: Number(id) } }));
        const settings = T().settings || {};
        if (!drawer || settings.afterAddToCart === 'page' || settings.cartType === 'page') { location.href = T().routes ? T().routes.cart : '/cart'; return; }
        say(form && form.dataset.added ? form.dataset.added : 'Added to your bag', true);
        drawer.open(opener);
      } catch (err) {
        say(err.message || 'Sorry, that size could not be added.');
        document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));   // the cart may have changed anyway
      } finally {
        if (form) form.classList.remove('is-busy');
      }
    },
  };
  // the form posts to /cart/add without JavaScript; with it, the bag opens in place
  let lastSubmitter = null;
  document.addEventListener('click', (e) => { const b = e.target.closest && e.target.closest('form.md-add button[name="id"]'); if (b) lastSubmitter = b; }, true);
  document.addEventListener('submit', (e) => {
    const f = e.target.closest && e.target.closest('form.md-add');
    if (!f) return;
    e.preventDefault();
    const b = (e.submitter && e.submitter.name === 'id' && e.submitter) || (lastSubmitter && f.contains(lastSubmitter) && lastSubmitter) || f.querySelector('button[name="id"]:not([disabled])');
    if (b && !b.disabled) MD.cart.add(b.value, b, f);
  });

  // ---------- sign-up: joins the same Klaviyo list as the pop-up, so the welcome email goes out ----------
  document.addEventListener('submit', async (e) => {
    const f = e.target.closest && e.target.closest('form.md-sub');
    if (!f) return;
    e.preventDefault();
    const input = f.querySelector('input[type="email"]'), msg = f.querySelector('.md-form__msg');
    const email = (input.value || '').trim();
    const say = (t, ok) => { msg.textContent = t; msg.classList.toggle('is-ok', !!ok); };
    if (!input.checkValidity() || !email) { input.reportValidity(); return; }
    if (!f.dataset.company || !f.dataset.list) { say(f.dataset.err); return; }
    const payload = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: f.dataset.source || 'Website sign-up',
          profile: { data: { type: 'profile', attributes: { email, subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } } } } },
        },
        relationships: { list: { data: { type: 'list', id: f.dataset.list } } },
      },
    };
    f.classList.add('is-busy');
    try {
      const res = await fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(f.dataset.company)}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json', revision: '2024-10-15' }, body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(String(res.status));
      f.classList.add('is-done');
      say(f.dataset.ok, true);
      msg.setAttribute('tabindex', '-1'); msg.focus({ preventScroll: true });   // the button it was on has gone
    } catch (err) {
      say(f.dataset.err);
    } finally {
      f.classList.remove('is-busy');
    }
  });

  // ---------- the header: Menu, logo, search, account, bag ----------
  class Header extends Section {
    setup() {
      this.section = this.closest('.shopify-section') || this;
      this.bar = this.querySelector('.md-bar');
      this.btn = this.querySelector('.md-menu-btn');
      this.menu = this.querySelector('.md-menu');
      this.label = this.querySelector('.md-mlabel');
      this.open = false;
      if (this.menu) MD.drawRibbons(this.menu);
      this.on(this.btn, 'click', () => this.toggle());
      this.on(document, 'keydown', (e) => { if (e.key === 'Escape' && this.open) this.toggle(false); });
      this.querySelectorAll('.md-menu__toggle').forEach((t) => this.on(t, 'click', () => {
        const x = t.getAttribute('aria-expanded') !== 'true', sub = document.getElementById(t.getAttribute('aria-controls'));
        t.setAttribute('aria-expanded', String(x)); if (sub) sub.hidden = !x;
      }));
      // a link to somewhere on this page closes the menu first
      this.on(this.menu, 'click', (e) => { const a = e.target.closest('a'); if (a && a.pathname === location.pathname && a.hash) this.toggle(false); });

      // search: Symmetry's own predictive search overlay (main-search.js), opened the way its header opens it
      const ms = this.querySelector('main-search');
      this.querySelectorAll('.md-search').forEach((a) => this.on(a, 'click', (e) => {
        if (!ms) return;
        e.preventDefault();
        if (this.open) this.toggle(false, true);
        if (T().manuallyLoadImages) T().manuallyLoadImages(ms);
        document.body.classList.add('show-search');
        setTimeout(() => { if (window.theme) theme.trapOpener = a; if (window.trapFocus) trapFocus(ms, ms.querySelector('.main-search__input')); }, 500);
      }));

      // bag: opens the cart drawer
      const bag = this.querySelector('.cart-link');
      this.on(bag, 'click', (e) => {
        const d = document.querySelector('.js-cart-drawer');
        if (!d || (T().settings && T().settings.cartType && T().settings.cartType !== 'drawer')) return;
        e.preventDefault();
        if (this.open) this.toggle(false, true);
        d.open(bag);
      });
      let t = 0;
      const update = () => { clearTimeout(t); t = setTimeout(() => MD.cart.refresh(), 150); };
      ['on:cart:add', 'on:cart:after-merge', 'on:cart:change'].forEach((ev) => this.on(document, ev, update));

      // header height for Symmetry's sticky columns, filters and add-to-cart bar
      if ('ResizeObserver' in window) { const ro = new ResizeObserver(() => this.measure()); ro.observe(this.section); this.obs.push(ro); }
      this.measure();

      // homepage: the bar lies over the opening film and takes the colour of the section under it
      this.overlay = this.dataset.overlay === 'true' && !!document.querySelector('#content .shopify-section:first-child :is(.md-hero, .md-cban--img)');
      this.classList.toggle('is-overlay', this.overlay);
      this.dataset.tone = this.overlay ? 'clear' : 'light';
      if (this.overlay) MD.loop.add(this);
    }
    measure() {
      const h = Math.ceil(this.section.getBoundingClientRect().height) || this.offsetHeight || 72;
      const root = document.documentElement.style;
      root.setProperty('--theme-header-height', h + 'px');
      root.setProperty('--theme-sticky-header-height', h + 'px');
      // anything above the bar (the announcement bar), so the opening film can fill exactly one screen
      let above = 0, s = this.section.previousElementSibling;
      while (s) { above += s.getBoundingClientRect().height; s = s.previousElementSibling; }
      root.setProperty('--md-above', Math.round(above) + 'px');
      this.secs = [...document.querySelectorAll('#content > .container > .shopify-section, #pagefooter > .shopify-section')];
    }
    frame() {
      if (this.open || !this.overlay) return;
      const y = this.bar.getBoundingClientRect().top + this.bar.offsetHeight / 2;
      let tone = 'light';
      for (const s of this.secs || []) {
        const r = s.getBoundingClientRect();
        if (r.top <= y && r.bottom > y) { const m = s.querySelector('[data-md-tone]'); tone = m ? m.dataset.mdTone : 'light'; break; }
      }
      if (this.dataset.tone !== tone) this.dataset.tone = tone;
    }
    toggle(force, keepFocus) {
      this.open = typeof force === 'boolean' ? force : !this.open;
      this.classList.toggle('is-menu', this.open);
      this.btn.setAttribute('aria-expanded', String(this.open));
      if (this.label) this.label.textContent = this.open ? this.label.dataset.close : this.label.dataset.open;
      this.menu.setAttribute('aria-hidden', String(!this.open));
      this.menu.inert = !this.open;
      // while the menu is open, nothing behind it can take focus (announcement bar, skip link, page, footer)
      if (this.open) { this._inerted = [...document.body.children].filter((n) => n !== this.section && !n.inert && n.tagName !== 'SCRIPT'); this._inerted.forEach((n) => { n.inert = true; }); }
      else { (this._inerted || []).forEach((n) => { n.inert = false; }); this._inerted = null; }
      MD.lockScroll(this.open);
      if (this.open) {
        const a = document.activeElement;   // Safari doesn't focus a tapped button, so fall back to the Menu button
        this._last = a && a !== document.body && !this.menu.contains(a) ? a : this.btn;
        document.body.classList.remove('show-search');
        const r = this.menu.querySelector('.md-ribbon');
        if (r) r.classList.add('in');
        setTimeout(() => { const a = this.menu.querySelector('a, button'); if (a) a.focus({ preventScroll: true }); }, 80);
      } else {
        if (!keepFocus && this._last && this._last.focus) this._last.focus({ preventScroll: true });
        this.frame();
      }
    }
    onBlockSelect() { if (!this.open) this.toggle(true); }
    onDeselect() { if (this.open) this.toggle(false); }
    teardown() { if (this.open) { MD.lockScroll(false); (this._inerted || []).forEach((n) => { n.inert = false; }); } }
  }
  MD.define('md-header', Header);

  // shop by collection: a photo of the collection appears beside the row you point at and drifts with the pointer
  class Cats extends Section {
    setup() {
      MD.reveal(this);
      this.rows = [...this.querySelectorAll('.md-cats__row')];
      this.pics = [...this.querySelectorAll('.md-cats__pic')];
      this.list = this.querySelector('.md-cats__rows');
      this.prev = this.querySelector('.md-cats__preview');
      if (!this.prev || !this.pics.length) return;
      const show = (i) => { this.pics.forEach((p, k) => p.classList.toggle('is-on', k === i)); this.classList.add('is-peek'); };
      const hide = () => this.classList.remove('is-peek');
      this.rows.forEach((r, i) => {
        this.on(r, 'pointerenter', () => show(i));
        this.on(r, 'focusin', () => { show(i); this.place(r.getBoundingClientRect().top + r.offsetHeight / 2); });
      });
      this.on(this.list, 'pointerleave', hide);
      this.on(this.list, 'focusout', (e) => { if (!this.list.contains(e.relatedTarget)) hide(); });
      this.on(this.list, 'pointermove', (e) => this.place(e.clientY));
    }
    place(y) {
      // the photo's centre follows the pointer, kept inside the rows
      const b = this.querySelector('.md-cats__body').getBoundingClientRect(), h = this.prev.offsetHeight;
      const top = clamp(y - b.top - h / 2, 0, Math.max(0, b.height - h));
      this.prev.style.setProperty('--py', (reduce.matches ? Math.round(top / 80) * 80 : top).toFixed(0) + 'px');
    }
  }
  MD.define('md-cats', Cats);

  // footer and plain sections: ribbons and reveals only
  class Plain extends Section {
    setup() { MD.drawRibbons(this); MD.reveal(this); }
  }
  MD.define('md-plain', Plain);

  // the rolling logo strip on phones stops when tapped (and again to restart)
  document.addEventListener('click', (e) => { const t = e.target.closest && e.target.closest('.md-stock__track'); if (t) t.classList.toggle('is-paused'); });

  // sections that move with the scroll: the three-photo zoom, slow parallax on framed photos, script lines whose
  // trailing dots appear one at a time (press page: 'every time.....')
  class Motion extends Section {
    setup() {
      MD.drawRibbons(this); MD.settle(this);
      this.querySelectorAll('.md-dots-auto').forEach((p) => {
        const m = p.textContent.trim().match(/^(.*?)(…|\.{2,})$/);
        if (!m) return;
        const n = m[2] === '…' ? 3 : m[2].length;
        p.textContent = m[1];
        const box = document.createElement('span'); box.className = 'md-dots';
        for (let k = 0; k < n; k++) { const d = document.createElement('span'); d.className = 'md-dot'; d.style.setProperty('--k', k); d.textContent = '.'; box.append(d); }
        p.append(box);
      });
      MD.reveal(this);
      this.trio = this.querySelector('.md-trio__row');
      this.px = [...this.querySelectorAll('.md-px')];
      if (!reduce.matches && (this.trio || this.px.length)) MD.loop.add(this);
    }
    frame(vh) {
      const t = this.trio;
      if (t) {
        // the middle photo grows by up to 30% as you scroll past; room is kept below it so it never runs into the next section
        const m = t.querySelector('.md-trio__fig--m');
        if (m) {
          const ch = m.offsetHeight, grow = 0.3;
          // measure without the padding already added, or the reserve flips between on and off every frame
          const reserve = Math.round(Math.max(0, ch * (1 + grow) - (t.offsetHeight - (t._reserve || 0))));
          if (t._reserve !== reserve) { t.style.paddingBottom = reserve + 'px'; t._reserve = reserve; }   // padding, not margin, or the grown photo is clipped
          const r = t.getBoundingClientRect(), s = 1 + grow * clamp((vh - r.top) / (vh + ch * 0.55), 0, 1);
          t.style.setProperty('--cs', s.toFixed(4));
          t.style.setProperty('--tx', ((m.offsetWidth * (s - 1)) / 2).toFixed(1) + 'px');
        }
      }
      this.px.forEach((img) => {
        const r = img.parentElement.getBoundingClientRect();
        if (r.bottom < -100 || r.top > vh + 100) return;
        const p = clamp((r.top + r.height / 2 - vh / 2) / vh, -1, 1);
        img.style.setProperty('--py', (-p * 5).toFixed(2) + '%');
      });
    }
  }
  MD.define('md-motion', Motion);

  // product rows: arrows step the row a screen at a time, and switch off at either end
  class Row extends Section {
    setup() {
      MD.reveal(this);
      const track = this.querySelector('.md-row__track');
      const prev = this.querySelector('[data-dir="-1"]'), next = this.querySelector('[data-dir="1"]');
      if (!track) return;
      const ends = () => {
        const max = track.scrollWidth - track.clientWidth - 2;
        if (prev) prev.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft >= max;
      };
      [prev, next].forEach((b) => this.on(b, 'click', () => {
        track.scrollBy({ left: Number(b.dataset.dir) * track.clientWidth * 0.85, behavior: reduce.matches ? 'auto' : 'smooth' });
      }));
      this.on(track, 'scroll', ends, { passive: true });
      this.on(window, 'resize', ends);
      ends();
    }
  }
  MD.define('md-row', Row);
  // rows that scroll sideways on phones (quotes, journal) can be reached and scrolled with the keyboard too
  const scrollRows = () => document.querySelectorAll('.md-quotes__list, .md-journal__grid').forEach((el) => {
    if (el.scrollWidth > el.clientWidth + 4) { el.tabIndex = 0; if (!el.getAttribute('aria-label')) { const h = (el.closest('.md') || el.parentElement).querySelector('h2'); el.setAttribute('aria-label', h ? h.textContent.trim().replace(/\s+/g, ' ') : 'List'); } }
    else el.removeAttribute('tabindex');
  });
  // phones: the service promises share one line and take turns; wider screens show them side by side
  const servRows = () => document.querySelectorAll('.md-serv__list').forEach((list) => {
    const items = [...list.children];
    if (items.length < 2) return;
    const phone = matchMedia('(max-width: 767.98px)').matches && !reduce.matches;
    if (!phone) { if (list._rot) { clearInterval(list._rot); list._rot = 0; } list.classList.remove('is-live'); items.forEach((li) => li.classList.remove('is-on')); return; }
    if (list._rot) return;
    let k = 0; items[0].classList.add('is-on'); list.classList.add('is-live');
    list._rot = setInterval(() => { if (document.hidden) return; items[k].classList.remove('is-on'); k = (k + 1) % items.length; items[k].classList.add('is-on'); }, 3400);
  });
  let rowsT = 0;
  const rowsSoon = () => { clearTimeout(rowsT); rowsT = setTimeout(() => { scrollRows(); servRows(); }, 200); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', rowsSoon); else rowsSoon();
  window.addEventListener('resize', rowsSoon); document.addEventListener('shopify:section:load', rowsSoon);
})();
